import React, { useEffect, useMemo, memo, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';

import { webSocketService } from 'services/WebSocketService';
import { Ball, Button, Input } from 'components/atoms';
import {
  DrawResult,
  Alert,
  ConnectionStatusLabel,
  TableChat,
  RouletteResult,
  WindowBurnShuffleCard,
  ClassicBaccaratResult,
} from 'components/molecules';

import {
  getGameData,
  getGameMachineConnection,
  getSelectedTableData,
  getSocketDisconnected,
} from 'core/selectors';
import {
  confirmResultAction,
  startDrawAction,
  startRoundAction,
  connectToWebSocketAction,
  tableClosedAction,
  disconnectFromWebSocketAction,
  clearGameDataAction,
  clearSelectedTableToStore,
  clearSagaChannels,
  connectToChatWebSocketAction,
  changeTimeLeftAction,
  startDecrementTimeLeftAction,
  startIncrementTimeLeftAction,
  stopDecrementTimeLeftAction,
  stopIncrementTimeLeftAction,
  getTableRequest,
  addNewCardAction,
  requestShuffleAction,
  setIsShuffleRequiredAction,
  setIsBurnCardWindowAction,
} from 'data/reducers';
import { getTimeLeft, getDrawTimeLeft, getRouletteColor } from 'helpers';
import { ICustomError, ResultBall, TableStates, ToastTypes } from 'types';
import { GameType, TimerTypes, ToastItemType } from 'types/tables';

import {
  useRequestError,
  useRequestLoading,
  useOnRequestSuccess,
  useButtonControls,
  useConnectionInfo,
} from 'hooks';
import { gameStatus } from 'constants/common';

import { getAuthSelector } from 'core/selectors/authSelectors';
import { JPWinner } from 'components/molecules/DrawResult/styles';
import {
  BallWrapper,
  CloseNotificationWrapper,
  ContentWrapper,
  ControlsWrapper,
  HistoryResult,
  TableControlsStyled,
  TableHeader,
  TableHeadStyled,
  TableStatusInfoStyled,
  ViewWrapper,
  Wrapper,
  ConnectionStatusWrapper,
  AlertWrapper,
  RouletteResultElement,
  ResultNumber,
  BarcodeForm,
  BarcodeFormButton,
  Block,
  BlockCards,
} from './styles';
import { TablePagePopups, TablePopupControls } from './TablePagePopups';

const burn_card_amount = 1;

export const TablePage = memo(() => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const auth = useSelector(getAuthSelector);

  const { id: urlTableId } = useParams();
  const selectedTableData = useSelector(getSelectedTableData);
  const hasFetchedRef = useRef(false);

  const connectionInfo = useConnectionInfo();

  const isGameMachineConnected = useSelector(getGameMachineConnection);

  const isSocketDisconnected = useSelector(getSocketDisconnected);
  const isConnectionChatLoading = useRequestLoading(connectToChatWebSocketAction);

  const {
    game_id: tableId,
    roundId,
    auto_confirm_result,
    dealer,
    tableState,
    statusText,
    betting_time,
    displaying_drawn_result,
    displaying_win_result,
    game_type,
    rng,
    round: {
      timeValue,
      drawResults,
      winner,
      timerLeft,
      drawStartedAt,
      finishBettingTime,
      finishDrawnDisplay,
      finishWinDisplay,

      cards,
      activeCardSlot,
      hasUndefinedCard,
      isBurnCardWindow,
    },
    toastItems,
    chatMessages,
    isShuffleRequired,
  } = useSelector(getGameData);

  const timeLeftValue = useMemo(() => {
    if (!auto_confirm_result && timeValue) {
      return undefined;
    }
    if (game_type === GameType.ROULETTE && tableState !== TableStates.DrawStarted) {
      return undefined;
    }
    if (game_type === GameType.CLASSIC_BAC && tableState === TableStates.DrawStarted) {
      return undefined;
    }
    return timerLeft;
  }, [auto_confirm_result, timeValue, timerLeft, game_type, tableState]);

  const {
    enabledButtons,
    isChangeDealerEnabled,
    onOpenRoundClick,
    onStartDrawClick,
    onConfirmResultClick,
  } = useButtonControls();

  const { error: connectToWebSocketError, clearError: connectToWebSocketClearError } =
    useRequestError(connectToWebSocketAction);
  const { error: connectToChatWebSocketError } = useRequestError(connectToChatWebSocketAction);

  const openRoundLoading = useRequestLoading(startRoundAction);
  const startDrawLoading = useRequestLoading(startDrawAction);
  const confirmResultLoading = useRequestLoading(confirmResultAction);

  useOnRequestSuccess(tableClosedAction, () => {
    navigate('/tables', { replace: true });
    dispatch(disconnectFromWebSocketAction());
    dispatch(clearSagaChannels());
    dispatch(clearGameDataAction());
    dispatch(clearSelectedTableToStore());
  });

  useEffect(() => {
    if (!timerLeft || (game_type === GameType.ROULETTE && tableState !== TableStates.DrawStarted)) {
      return;
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        console.log(`User has left the page on ${tableState}`);
        dispatch(stopIncrementTimeLeftAction());
        dispatch(stopDecrementTimeLeftAction());
      } else {
        console.log(`User has returned to the page on ${tableState}`);
        dispatch(stopIncrementTimeLeftAction());
        dispatch(stopDecrementTimeLeftAction());

        if (tableState === TableStates.BettingTimeStarted) {
          dispatch(
            changeTimeLeftAction({
              timerLeft: getTimeLeft(finishBettingTime, betting_time),
            }),
          );
          dispatch(startDecrementTimeLeftAction({ type: TimerTypes.bettingTime }));
        }
        if (tableState === TableStates.DrawFinished) {
          dispatch(
            changeTimeLeftAction({
              timerLeft: getTimeLeft(finishDrawnDisplay, displaying_drawn_result),
            }),
          );
          dispatch(startDecrementTimeLeftAction({ type: TimerTypes.displayingDrawn }));
        }
        if (tableState === TableStates.ResultConfirmed) {
          dispatch(
            changeTimeLeftAction({
              timerLeft: getTimeLeft(finishWinDisplay, displaying_win_result),
            }),
          );
          dispatch(startDecrementTimeLeftAction({ type: TimerTypes.displayingWin }));
        }
        if (tableState === TableStates.DrawStarted) {
          dispatch(changeTimeLeftAction({ timerLeft: getDrawTimeLeft(drawStartedAt) }));
          dispatch(startIncrementTimeLeftAction());
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [
    dispatch,
    timerLeft,
    tableState,
    drawStartedAt,
    finishBettingTime,
    finishDrawnDisplay,
    finishWinDisplay,
    betting_time,
    displaying_drawn_result,
    displaying_win_result,
    game_type,
  ]);

  useEffect(() => {
    if (tableState !== TableStates.DrawStarted) {
      dispatch(stopIncrementTimeLeftAction());
    }
  }, [dispatch, tableState]);

  useEffect(() => {
    const error = connectToWebSocketError as ICustomError;
    if (error?.message === 'Unauthorized' || error?.status === 404) {
      connectToWebSocketClearError();
      if (urlTableId) {
        navigate(`/tables/${urlTableId}/error`, { replace: true });
      }
      if (!urlTableId) {
        navigate(`/not-found`, { replace: true });
      }
    }
  }, [connectToWebSocketError, connectToWebSocketClearError, navigate, urlTableId]);

  useEffect(() => {
    if (!urlTableId || hasFetchedRef.current) return;

    if (!webSocketService.isConnected() && urlTableId) {
      hasFetchedRef.current = true;
      dispatch(getTableRequest({ id: urlTableId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!webSocketService.isConnected() && urlTableId && selectedTableData?.game_type) {
      const tableGameType = selectedTableData.game_type;

      dispatch(clearSagaChannels());
      dispatch(
        connectToWebSocketAction({
          tableId: urlTableId,
          token: sessionStorage.getItem('access_to_ws_token')!,
          gameType: tableGameType,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableData]);

  const nextJPDate = useMemo(
    () =>
      auth?.currentJackpot ? moment(auth?.currentJackpot.drawnDate).format('ddd, MMM Do') : '',
    [auth?.currentJackpot],
  );

  const resultLogItems = useMemo(
    () =>
      drawResults.map((draw, index) => {
        if (game_type === GameType.ROULETTE) {
          return (
            <RouletteResultElement
              key={`${draw}-${index}`}
              $color={getRouletteColor(draw)}
              $isLast={index === drawResults.length - 1}>
              <ResultNumber>{draw}</ResultNumber>
            </RouletteResultElement>
          );
        }

        if (game_type === GameType.BAC) {
          return (
            <BallWrapper key={`${draw}-${index}`}>
              <Ball value={draw as ResultBall} />
            </BallWrapper>
          );
        }

        return null;
      }),
    [drawResults, game_type],
  );

  const winnerResultElement = useMemo(() => {
    if (game_type === GameType.ROULETTE) {
      return <RouletteResult value={{ color: getRouletteColor(winner), number: winner }} />;
    }
    if (game_type === GameType.BAC) {
      return <DrawResult value={winner} />;
    }
    return null;
  }, [game_type, winner]);

  const [popupControls, setPopupControls] = useState<TablePopupControls | null>(null);
  const [isAnyPopupOpen, setIsAnyPopupOpen] = useState(false);

  const scanCardText = useMemo(() => {
    if (game_type === GameType.CLASSIC_BAC && tableState === TableStates.DrawStarted) {
      return gameStatus.ScanCards;
    }

    return null;
  }, [game_type, tableState]);

  const [burnCardClosed, setBurnCardClosed] = useState(false);
  const [cardInput, setCardInput] = useState<any>('');

  const [scannedText, setScannedText] = useState('');
  const [scannedTempText, setScannedTempText] = useState('');
  const [scannedCodes, setScannedCodes] = useState<string[]>([]);

  useEffect(() => {
    if (tableState === TableStates.BettingTimeStarted) {
      setCardInput('');
      setBurnCardClosed(false);
    }
  }, [tableState]);

  useEffect(() => {
    if (tableState === TableStates.DrawStarted) {
      setScannedCodes([]);
      setScannedText('');
    }
  }, [tableState]);

  const addCard = () => {
    setScannedCodes([...scannedCodes, cardInput]);
    setCardInput('');
    dispatch(addNewCardAction({ barcode: cardInput }));
  };

  const changeText = useCallback(
    (key: string) => {
      if (key === 'Shift' && !scannedText?.length) {
        return setScannedText(`${scannedText}${key}`);
      }
      if (key === 'Shift') {
        setScannedTempText(scannedTempText);
        return setScannedText(`${scannedText}${key}`);
      }
      if (key === 'Enter') {
        setScannedTempText('');
        setScannedCodes([...scannedCodes, scannedTempText]);
        dispatch(addNewCardAction({ barcode: scannedTempText }));
        return setScannedText(`${scannedText}${key}`);
      }
      setScannedTempText(`${scannedTempText}${key}`);
      return setScannedText(`${scannedText}${key}`);
    },
    [scannedText, scannedTempText, scannedCodes, dispatch],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        tableState === TableStates.DrawStarted &&
        !isAnyPopupOpen &&
        !isBurnCardWindow &&
        !isShuffleRequired
      ) {
        changeText(event.key);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [changeText, tableState, isAnyPopupOpen, isBurnCardWindow, isShuffleRequired]);

  useEffect(() => {
    if (
      (timerLeft === 6 || (timerLeft < 6 && !isBurnCardWindow && !burnCardClosed)) &&
      tableState === TableStates.BettingTimeStarted
    ) {
      dispatch(setIsBurnCardWindowAction(true));
    }
  }, [burnCardClosed, timerLeft, tableState, isBurnCardWindow, dispatch]);

  const onShuffleClick = useCallback(() => {
    dispatch(requestShuffleAction());
  }, [dispatch]);

  const onCloseBurnCardWindow = useCallback(() => {
    dispatch(setIsBurnCardWindowAction(false));
    setBurnCardClosed(true);
  }, [dispatch]);

  const onCloseShuffleWindow = useCallback(() => {
    dispatch(setIsShuffleRequiredAction(false));
  }, [dispatch]);

  return (
    <Wrapper>
      <TablePagePopups onMounted={setPopupControls} onPopupStateChange={setIsAnyPopupOpen} />

      <TableHeader>
        <TableHeadStyled
          tableId={tableId}
          roundId={roundId}
          dealerName={dealer?.nickname}
          isChangeDealerDisabled={!isChangeDealerEnabled}
          onChangeDealerClick={popupControls?.showChangeDealer}
          onCloseTableClick={popupControls?.showCloseTableVerify}
          stage={auth?.stage}
          dateTimer={auth?.countdownJackpot}
          connectionInfo={connectionInfo}
          nextJPDate={nextJPDate}
          onShuffleClick={onShuffleClick}
          isShuffleDisabled={rng}
          gameType={game_type}
        />
      </TableHeader>
      <ContentWrapper>
        <ControlsWrapper>
          <TableStatusInfoStyled
            timeValue={timeLeftValue}
            statusText={statusText}
            status={tableState}
            additionalText={scanCardText}
          />
          <TableControlsStyled
            onOpenRoundClick={onOpenRoundClick}
            openRoundLoading={openRoundLoading}
            onStartDrawClick={onStartDrawClick}
            startDrawLoading={startDrawLoading}
            onConfirmResultClick={onConfirmResultClick}
            confirmResultLoading={confirmResultLoading}
            onChangeResultClick={popupControls?.showChangeResultVerify}
            onCancelRoundClick={popupControls?.showCancelRound}
            enabledButtons={enabledButtons}
            gameType={game_type}
          />
          {toastItems.length > 0 ? (
            <CloseNotificationWrapper>
              {toastItems.map((toastItem: ToastItemType) => (
                <AlertWrapper key={toastItem.value.text}>
                  <Alert variant={toastItem.type ? toastItem.type : ToastTypes.WARNING} showIcon>
                    {toastItem.value.text}
                  </Alert>
                </AlertWrapper>
              ))}
            </CloseNotificationWrapper>
          ) : null}
        </ControlsWrapper>
        <ViewWrapper>
          {auth?.isJPDrawn && <JPWinner>Jackpot drawn</JPWinner>}
          {winner !== null && winner !== undefined ? winnerResultElement : null}

          {game_type === GameType.CLASSIC_BAC && (
            <ClassicBaccaratResult
              cards={cards || {}}
              activeSlot={activeCardSlot}
              hasUndefinedCard={hasUndefinedCard}
              winner={winner}
            />
          )}
          <ConnectionStatusWrapper>
            {game_type !== GameType.CLASSIC_BAC && (
              <ConnectionStatusLabel
                isConnected={!isSocketDisconnected ? isGameMachineConnected : !isSocketDisconnected}
                statusText="MACHINE"
              />
            )}
            <ConnectionStatusLabel isConnected={!isSocketDisconnected} statusText="SERVER" />
          </ConnectionStatusWrapper>

          {resultLogItems.length ? (
            <HistoryResult gameType={game_type}>{resultLogItems}</HistoryResult>
          ) : null}

          <TableChat
            messages={chatMessages}
            isLoading={isConnectionChatLoading}
            connectionError={connectToChatWebSocketError?.message}
          />
        </ViewWrapper>
      </ContentWrapper>
      {game_type === GameType.CLASSIC_BAC &&
        !rng &&
        tableState === TableStates.DrawStarted &&
        !isShuffleRequired &&
        !isBurnCardWindow && (
          <ContentWrapper>
            <BarcodeForm>
              <Input
                label="Barcode"
                hasError={false}
                message=""
                name="Barcode"
                placeholder="Enter barcode"
                value={cardInput}
                onChange={(event: any) => setCardInput(event.target.value.toUpperCase())}
              />
              <BarcodeFormButton>
                <Button onClick={addCard}>Add card</Button>
              </BarcodeFormButton>
            </BarcodeForm>
            <div>
              <Block>
                <BlockCards>Scanned barcodes:</BlockCards>{' '}
                {scannedCodes?.map((code) => `${code}, `)}
              </Block>
            </div>
          </ContentWrapper>
        )}

      {game_type === GameType.CLASSIC_BAC && !rng && (
        <WindowBurnShuffleCard
          isBurnCardWindow={isBurnCardWindow}
          isShuffleWindow={isShuffleRequired}
          closeBurnCardWindow={onCloseBurnCardWindow}
          closeShuffleWindow={onCloseShuffleWindow}
          burnCardAmount={burn_card_amount}
        />
      )}
    </Wrapper>
  );
});
